import React from 'react';
import SEO from '../components/SEO';

const PRIVACY_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://phennx.io/privacy-policy#webpage',
      url: 'https://phennx.io/privacy-policy',
      name: 'Privacy Policy',
      description: 'Learn how we collect, use, and protect personal information on the Phennx website.',
      isPartOf: { '@id': 'https://phennx.io/#website' },
      about: { '@id': 'https://phennx.io/#organization' },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://phennx.io/privacy-policy#breadcrumbs',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://phennx.io/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Privacy Policy',
          item: 'https://phennx.io/privacy-policy',
        },
      ],
    },
  ],
};

export default function PrivacyPolicyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Learn how we collect, use, and protect personal information on the Phennx website."
        path="/privacy-policy"
        image="/logo-full-transparent.png"
        type="article"
        schema={PRIVACY_SCHEMA}
      />
      <article className="legal-page" aria-label="Privacy Policy">
        <header className="legal-page__hero">
          <div className="section-inner">
            <div className="legal-page__eyebrow">Legal</div>
            <h1 className="legal-page__title">Privacy Policy</h1>
            <p className="legal-page__lede">
              Please read this policy carefully to understand how we treat your personal information and your choices
              and rights.
            </p>
            <p className="legal-page__updated">Last updated: November 1, 2024</p>
          </div>
        </header>

        <section className="legal-page__content">
          <div className="section-inner">
            <div className="legal-page__section">
              <h2>Introduction</h2>
              <p>
                PHENNX.IO, LLC (also referred to herein as &quot;we,&quot; &quot;us,&quot; and
                &quot;our&quot;) is committed to protecting the privacy and security of the personal information we
                collect, use, share, and otherwise process as part of our operations. We also believe in transparency,
                and we are committed to informing you about how we treat the data we collect and process.
              </p>
              <p>
                This Privacy Policy (the &quot;Policy&quot;) describes our practices regarding your personal information
                when you visit our website, phennx.io (the &quot;Site&quot;). This Policy does not apply to Protected
                Health Information.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>What information do we collect?</h2>
              <p>
                We collect the following information for the purposes described in the &quot;How Do We Use and Share
                Your Information?&quot; section of this Policy.
              </p>
              <ul className="legal-page__list">
                <li>
                  <strong>Contact Information.</strong> When you visit our Site or contact us, we may collect or receive
                  your name, email address, and telephone number.
                </li>
                <li>
                  <strong>Employment Application Information.</strong> When you apply for employment with us, we may
                  collect your name, mailing address, email address, telephone number, work history, criminal history,
                  names of references and other relevant information.
                </li>
                <li>
                  <strong>Communications, Feedback, Support, and Inquiries.</strong> If you contact us via the Site, in
                  addition to your Contact Information, we will receive the subject matter of your message and any
                  comments, content, or other information that you choose to provide.
                </li>
                <li>
                  <strong>Cookies and Similar Technologies.</strong> The Site uses cookies and similar technologies.
                  Please see the &quot;Cookies and Similar Technologies&quot; section of this Policy for more
                  information.
                </li>
                <li>
                  <strong>Usage Data.</strong> When you use the Site, we automatically collect information from your
                  browser or device, which includes the date and time of access, your location, your Internet Protocol
                  (IP) address or unique device identifier, your device type, your operating system, your browser type,
                  and information about what pages you visit.
                </li>
              </ul>
            </div>

            <div className="legal-page__section">
              <h2>Cookies and similar technologies</h2>
              <h3>First-party and third-party cookies</h3>
              <p>
                A &quot;cookie&quot; is a small file created by a web server that can be stored on your device (if you
                allow) for use either during a particular browsing session (a &quot;session&quot; cookie) or a future
                browsing session (a &quot;persistent&quot; or &quot;permanent&quot; cookie). &quot;Session&quot; cookies
                are temporarily stored on your hard drive and only last until they expire at the end of your browsing
                session. &quot;Persistent&quot; or &quot;permanent&quot; cookies remain stored on your hard drive until
                they expire or are deleted by you. Local stored objects (or &quot;flash&quot; cookies) are used to
                collect and store information about your preferences and navigation to, from, and on a website.
                First-party cookies are set by the website you are visiting, and they can only be read by that site.
                Third-party cookies are set by a party other than that website.
              </p>

              <h3>Similar technologies</h3>
              <p>
                In addition to cookies, there are other automatic data collection technologies, such as Internet tags,
                web beacons (clear gifs, pixel tags, and single-pixel gifs), and navigational data collection (log files,
                server logs, etc.) that can be used to collect data as users navigate through and interact with a
                website.
              </p>
              <ul className="legal-page__list">
                <li>
                  <strong>Web beacons.</strong> These are tiny graphics (sometimes called &quot;clear GIFs&quot; or
                  &quot;web pixels&quot;) with unique identifiers that are used to understand browsing activity. In
                  contrast to cookies, which are stored on a user&apos;s device, web beacons are rendered invisible on
                  web pages when you open a page.
                </li>
                <li>
                  <strong>Social Widgets.</strong> These are buttons or icons provided by third-party social media
                  platforms that allow you to interact with social media services when you view a webpage. These social
                  widgets may collect browsing data, which may be received by the third party that provided the widget
                  and are controlled by third parties.
                </li>
                <li>
                  <strong>UTM Codes.</strong> These are strings that can appear in a URL (the Uniform Resource Locator,
                  which is typically the http or https address entered to go to a webpage) when you move from one web
                  page or website to another. The string can represent information about browsing, such as which
                  advertisement, page, or publisher sent the user to the receiving website.
                </li>
              </ul>

              <h3>What cookies and similar technologies are in use and why do we use them?</h3>
              <p>
                We use first-party and third-party cookies and similar technologies for purposes such as to improve Site
                functionality, to measure and track how users interact with the Site and perform similar analytics, to
                track ad-driven activity on the Site, and to otherwise tailor our communications with you.
              </p>
              <ul className="legal-page__list">
                <li>
                  <strong>Facebook.</strong> We use Facebook&apos;s technologies to serve relevant advertisements and to
                  track Facebook ad-driven visitor activity on the Site. Facebook Connect also gives you the option to
                  post information about your activities to your profile page to share with others within your network
                  by using a single sign-in service to authenticate your identity. You may learn more about Facebook&apos;s
                  policies by visiting{' '}
                  <a href="https://www.facebook.com/policy.php">https://www.facebook.com/policy.php</a>, and you can also
                  find instructions for opting out of receiving advertisements via that page.
                </li>
                <li>
                  <strong>Google Analytics.</strong> We use Google Analytics to collect and process statistical data
                  about the number of people using the Site and to better understand how they find and use the Site. The
                  data collected includes data related to your device/browser, your IP address, and on-site activities
                  to measure and report statistics about user interactions on the Site. The information stored in the
                  cookie is reduced to a random identifier. Any data collected is used in accordance with this Policy
                  and Google&apos;s privacy policy. You may learn more about Google Analytics by visiting{' '}
                  <a href="https://www.google.com/policies/privacy/partners/">
                    https://www.google.com/policies/privacy/partners/
                  </a>{' '}
                  and{' '}
                  <a href="https://support.google.com/analytics/answer/6004245">
                    https://support.google.com/analytics/answer/6004245
                  </a>
                  . You can learn more about Google&apos;s restrictions on data use by visiting the Google Privacy Policy
                  at: <a href="https://www.google.com/policies/privacy">https://www.google.com/policies/privacy</a>. To
                  opt-out of Google Analytics, visit{' '}
                  <a href="https://tools.google.com/dlpage/gaoptout">
                    https://tools.google.com/dlpage/gaoptout
                  </a>{' '}
                  and install the opt-out browser add-on feature. For more details, visit the &quot;Google Analytics
                  opt-out browser add-on&quot; page located at{' '}
                  <a href="https://support.google.com/analytics/answer/181881?hl=en">
                    https://support.google.com/analytics/answer/181881?hl=en
                  </a>
                  .
                </li>
                <li>
                  <strong>Google Marketing.</strong> We use Google&apos;s marketing technologies (DoubleClick) to track
                  user activity on the Site and to serve personalized advertisements. Your browser is assigned a
                  pseudonymous ID used to track the ads that have been served to your browser and to identify those on
                  which you&apos;ve clicked. The cookies enable Google and its partners to select and display ads based
                  on your browsing behavior. For more information on how Google uses this information, you can visit{' '}
                  <a href="https://policies.google.com/technologies/ads">
                    https://policies.google.com/technologies/ads
                  </a>
                  ,{' '}
                  <a href="https://support.google.com/displayvideo/answer/7621162">
                    https://support.google.com/displayvideo/answer/7621162
                  </a>
                  , and{' '}
                  <a href="https://support.google.com/google-ads/answer/7548399?hl=en">
                    https://support.google.com/google-ads/answer/7548399?hl=en
                  </a>
                  . To block certain ads served by Google, please visit{' '}
                  <a href="https://support.google.com/ads/answer/2662922">
                    https://support.google.com/ads/answer/2662922
                  </a>
                  . Google&apos;s Privacy Policy is available at:{' '}
                  <a href="https://www.google.com/policies/privacy">https://www.google.com/policies/privacy</a>.
                </li>
              </ul>

              <h3>Other third-party technologies</h3>
              <p>
                Some third parties may use automated data collection technologies to collect information about you when
                you browse the Internet. The information they collect about your online browsing activities over time
                and across different websites and other online services may be associated with your personal information
                and used to provide you with targeted content. We do not control these third parties&apos; technologies
                or how they may be used. If you have any questions about targeted content, you should contact the
                responsible party directly or consult their privacy policies.
              </p>

              <h3>Choices about cookies</h3>
              <p>
                Most web browsers are set by default to accept cookies. If you do not wish to receive cookies, you may
                set your browser to refuse all or some types of cookies or to alert you when cookies are being sent by
                website tracking technologies and advertising. You may adjust your browser settings to opt out of
                accepting a &quot;persistent&quot; cookie and to only accept &quot;session&quot; cookies, but you will
                need to log in each time you want to enjoy the full functionality of the Site.
              </p>
              <p>
                Please be aware that, if you decline the use of cookies, you may not have access to the full benefits of
                the Site, and certain features of the Site may not work properly. In addition, adjusting the cookie
                settings on the Site may not fully delete all of the cookies that have already been created. To delete
                them, visit your web browser settings after you have changed your cookie settings on the Site.
              </p>
              <p>
                Additional information is provided below about how to disable cookies or manage the cookie settings for
                some of the leading web browsers:
              </p>
              <ul className="legal-page__list">
                <li>
                  <strong>Google Chrome:</strong>{' '}
                  <a href="https://support.google.com/chrome/answer/95647?hl=en">
                    https://support.google.com/chrome/answer/95647?hl=en
                  </a>
                </li>
                <li>
                  <strong>Firefox:</strong>{' '}
                  <a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences">
                    https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences
                  </a>
                </li>
                <li>
                  <strong>Internet Explorer:</strong>{' '}
                  <a href="http://windows.microsoft.com/en-GB/windows-vista/Block-or-allow-cookies">
                    http://windows.microsoft.com/en-GB/windows-vista/Block-or-allow-cookies
                  </a>
                </li>
                <li>
                  <strong>Safari:</strong>{' '}
                  <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac">
                    https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac
                  </a>{' '}
                  and{' '}
                  <a href="https://support.apple.com/en-us/HT201265">
                    https://support.apple.com/en-us/HT201265
                  </a>
                </li>
              </ul>
              <p>
                To learn how you can manage your Flash cookie settings, visit the Flash player settings page on
                Adobe&apos;s website at:{' '}
                <a href="https://www.macromedia.com/support/documentation/en/flashplayer/help/help09.html">
                  https://www.macromedia.com/support/documentation/en/flashplayer/help/help09.html
                </a>
                . For more information on how to modify your browser settings to block or filter cookies, visit{' '}
                <a href="https://www.allaboutcookies.org/">https://www.allaboutcookies.org/</a> or{' '}
                <a href="http://www.cookiecentral.com/faq/">http://www.cookiecentral.com/faq/</a>. You may learn more
                about internet advertising practices and related consumer resources at{' '}
                <a href="http://www.aboutads.info/consumers/">http://www.aboutads.info/consumers/</a>,{' '}
                <a href="http://www.networkadvertising.org/choices">http://www.networkadvertising.org/choices</a>, and{' '}
                <a href="http://youronlinechoices.eu/">http://youronlinechoices.eu/</a>.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>How do we use and share your information?</h2>
              <p>
                <strong>How do we use the information described in this Policy?</strong> To the extent permitted by
                applicable law, we may use the information described in the &quot;What Information Do We Collect&quot;
                section above in order to:
              </p>
              <ul className="legal-page__list">
                <li>Operate and improve our business, services, and the Site.</li>
                <li>Provide you with services, content, and functionality.</li>
                <li>Honor our terms of use and contracts.</li>
                <li>Manage our relationship with you.</li>
                <li>Process and complete payments and other transactions.</li>
                <li>Respond to your request, including review of a job application.</li>
                <li>Improve our services and develop new services.</li>
                <li>Maintain our databases and back-ups, including records of our communications with you.</li>
                <li>Ensure the privacy and security of our Site and services.</li>
                <li>Detect fraud and prevent loss.</li>
                <li>Support and improve the Site, including evaluations of functionality and features.</li>
                <li>Improve our customer service.</li>
                <li>Communicate with you and respond to your feedback, requests, questions, or inquiries.</li>
                <li>Promote our services.</li>
                <li>Contact you about other products and services.</li>
                <li>Improve our marketing efforts, including by providing more tailored advertising.</li>
                <li>Administer a contest, promotion, or survey.</li>
                <li>Assess the success of our marketing and advertising campaigns.</li>
                <li>Analyze use of the Site and our services and prepare aggregate traffic information.</li>
                <li>Recognize your device and remember your preferences and interactions.</li>
                <li>Provide you with a more personal and interactive experience on the Site.</li>
                <li>Determine and track user interests, trends, needs, and preferences.</li>
                <li>
                  Accomplish any other purpose related to and/or ancillary to any of the purposes and uses described in
                  this Policy for which your information was provided to us.
                </li>
                <li>
                  Accomplish another purpose described to you when you provide the information, for which you have
                  consented, or for which we have a legal basis under law.
                </li>
                <li>Comply with federal, state, or local laws.</li>
                <li>
                  Comply with a civil, criminal, or regulatory inquiry, investigation, subpoena, order, or summons by
                  federal, state, or local authorities.
                </li>
                <li>Cooperate with law enforcement agencies.</li>
                <li>Exercise or defend legal rights or claims.</li>
                <li>Create, use, retain, or disclose de-identified or aggregated data.</li>
              </ul>

              <p>
                <strong>How do we disclose or share the information described in this Policy?</strong> Where permitted
                by applicable law, we may share the information described in the &quot;What Information Do We
                Collect&quot; section above in the following contexts:
              </p>
              <ul className="legal-page__list">
                <li>
                  <strong>Corporate Affiliates.</strong> We may share your information with our corporate subsidiaries
                  and affiliates and with their respective officers, directors, employees, professional advisors, and
                  agents.
                </li>
                <li>
                  <strong>Acquisitions and Similar Transactions.</strong> We may disclose your information in the event
                  of a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of
                  some or all of our assets, whether as a going concern or as part of bankruptcy, liquidation, or
                  similar proceeding.
                </li>
                <li>
                  <strong>Disclosures with Your Consent.</strong> We may ask if you would like us to share your
                  information with other unaffiliated third parties who are not described elsewhere in this Policy. We
                  will only disclose your information in this context with your consent.
                </li>
                <li>
                  <strong>Legal Obligations and Rights (Subpoenas, Court Orders, and Warrants).</strong> We may disclose
                  information in response to subpoenas, warrants, court orders or other legal process, or to comply with
                  relevant laws. We may also share information in order to establish or exercise our legal rights or
                  claims; to defend against a legal claim; and to investigate, prevent, or take action regarding
                  possible illegal activities, suspected fraud, safety of person or property, or a violation of our
                  contracts.
                </li>
                <li>
                  <strong>Third Parties.</strong> We may provide information about you to third parties that may offer
                  products and services specifically requested by you.
                </li>
                <li>
                  <strong>Service Providers.</strong> We may share information with our service providers that need
                  access to information to provide operational or other support services on our behalf. Among other
                  things, service providers help us to administer the Site; provide payment processing; support our
                  provision of services requested by you; provide technical support; send communications to you; and
                  assist with other legitimate purposes permitted by law.
                </li>
                <li>
                  <strong>Professional Advisors.</strong> We may share your information with our and our affiliates&apos;
                  insurers and other professional advisors, including attorneys and accountants, that need access to
                  your information to provide operational or other support services on our behalf.
                </li>
                <li>
                  <strong>Deidentified or Aggregated Data.</strong> We may disclose aggregated information or
                  information that does not identify any specific individual, such as groupings of demographic data or
                  customer preferences.
                </li>
              </ul>
            </div>

            <div className="legal-page__section">
              <h2>How long do we store and use your information?</h2>
              <p>
                We will retain and use your information for as long as is necessary to fulfill the purposes for which it
                was collected, to comply with our business requirements and legal obligations, to resolve disputes, to
                protect our assets, to provide our services, and to enforce our agreements.
              </p>
              <p>
                We may delete your information if we believe it is incomplete, inaccurate, or that our continued storage
                of it is contrary to our objectives or legal obligations. When we delete data, it will be removed from
                our active servers and databases, but it may remain in our archives when it is not practical or possible
                to delete it.
              </p>
              <p>
                To the extent permitted by law, we may retain and use anonymous, de-identified, or aggregated
                information for performance reporting, benchmarking, and analytic purposes and for product and service
                improvement.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>How do we protect your information?</h2>
              <p>
                We have put security measures in place to protect the personal information that you share with us from
                being accidentally lost, used, altered, disclosed, or accessed in an unauthorized manner. From time to
                time, we review our security procedures to consider appropriate new technologies and methods.
              </p>
              <p>
                While our security measures seek to protect the personal information in our possession, no security
                system is perfect, and no data transmission is 100% secure. As a result, while we strive to protect your
                information, we cannot guarantee or warrant the security of any information transmitted to or from the
                Site. Your use of the Site is at your own risk. We cannot guarantee that your data will remain secure in
                all circumstances.
              </p>
              <p>
                If a data breach compromises your personal information, we will notify you and any applicable regulator
                when we are required to do so by applicable law.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Your rights and choices regarding personal information</h2>
              <p>
                Please use the &quot;Contact Us&quot; details provided at the end of this Policy to exercise your rights
                and choices under this Policy. We honor such requests when we are required to do so under applicable
                law.
              </p>
              <ul className="legal-page__list">
                <li>
                  <strong>Communication Preferences.</strong> You may change your communication preferences at any time
                  by contacting us via the &quot;Contact Us&quot; details provided at the end of this Policy. If you no
                  longer wish to receive communications from us via email, you may opt-out by clicking the
                  &quot;unsubscribe&quot; link at the bottom of our emails. If you wish to change your communication
                  preferences, please provide your name, telephone number, and/or email address so that we may identify
                  you in the opt-out process. Once we receive your instruction, we will promptly take action.
                </li>
                <li>
                  <strong>Accuracy and Updating Your Information.</strong> Our goal is to keep your information
                  accurate, current, and complete. If any of the information you have provided to us changes, please let
                  us know via the &quot;Contact Us&quot; details at the end of this Policy. For instance, if your email
                  address changes, you may wish to let us know so that we can communicate with you. If you become aware
                  of inaccurate personal information about you, you may want to update your information. We are not
                  responsible for any losses arising from any inaccurate, inauthentic, deficient or incomplete personal
                  data that you provide to us.
                </li>
                <li>
                  <strong>Complaints.</strong> If you believe your rights relating to your personal information have
                  been violated, please contact us via the &quot;Contact Us&quot; details provided at the end of this
                  Policy.
                </li>
                <li>
                  <strong>Online Tracking Signals.</strong> We do not currently recognize browser settings or signals of
                  tracking preferences, which may include &quot;Do Not Track&quot; instructions. &quot;Do Not Track&quot;
                  is a web browser setting that seeks to disable the tracking of individual users&apos; browsing
                  activities. We adhere to the standards set out in this Policy and do not currently respond to
                  &quot;Do Not Track&quot; signals on the Site or on third-party websites or online services where we may
                  collect information.
                </li>
                <li>
                  <strong>Nevada Residents.</strong> Pursuant to our standard business practices, we do not make any
                  sales (as defined under Nevada law) of any covered information (as defined under Nevada law) that we
                  have collected or will collect about you. However, you may submit a verified request to us at
                  info@phennx.io to request that we not make any sale of covered information. Please provide your
                  name and contact information in your request, and we will respond to your request in accordance with
                  Nevada law.
                </li>
                <li>
                  <strong>California Residents.</strong> Under California Civil Code Section 1798.83, California
                  residents who provide personal information in obtaining products or services for personal, family, or
                  household use may be entitled to request and obtain from us once a calendar year information about the
                  information we shared, if any, with other businesses for direct marketing uses. At present, we do not
                  share your personal information with third parties for those third parties&apos; direct marketing
                  purposes. Please be aware that not all information sharing is covered by the &quot;Shine the Light&quot;
                  requirements and only information on covered sharing, if any, will be included in our response. As
                  part of the California Online Privacy Protection Act, all users of our Site may make any changes to
                  their information at any time by contacting us at info@phennx.io.
                </li>
              </ul>
            </div>

            <div className="legal-page__section">
              <h2>Third-party sites and services</h2>
              <p>
                This Policy only applies to the Site, and it does not apply to any third-party websites or applications.
              </p>
              <p>
                The Site may contain links to, and media or other content from, third parties. These links are to
                external resources and third parties that have their own privacy policies. Because of the dynamic media
                capabilities of the Site, it may not be clear to you which links are to external, third-party resources.
                If you click on an embedded third-party link, you will be redirected away from the Site to the external
                third-party website. You can check the URL to confirm that you have left this Site.
              </p>
              <p>
                We cannot and do not (1) guarantee the adequacy of the privacy or security practices employed by or the
                content and media provided by any third parties or their websites, (2) control third parties&apos;
                independent collection or use or your information, or (3) endorse any third-party information, products,
                services or websites that may be reached through embedded links on this Site.
              </p>
              <p>
                Any information provided by you or automatically collected from you by a third party will be governed by
                that party&apos;s privacy policy and terms of use. If you are unsure whether a website is controlled,
                affiliated, or managed by us, you should review the privacy policy and practices applicable to each
                linked website.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Children&apos;s Online Privacy Protection Act</h2>
              <p>
                The Children&apos;s Online Privacy Protection Act (&quot;COPPA&quot;), as well as other data privacy
                regulations, restrict the collection, use, or disclosure of personal information from and about children
                on the Internet. Our Site and services are not directed to children aged 13 or younger, nor is
                information knowingly collected from children under the age of 13. No one under the age of 13 may
                access, browse, or use the Site or provide any information to or on the Site (including, for example,
                your name, telephone number, email address, or username). If we learn that we have collected or received
                personal information from a child under the age of 13 without a parent&apos;s or legal guardian&apos;s
                consent, we will take steps to stop collecting that information and delete it. If you believe we might
                have any information from or about a child under the age of 13, please contact us via the &quot;Contact
                Us&quot; details provided at the end of this Policy.
              </p>
              <p>
                For more information about COPPA, please visit the Federal Trade Commission&apos;s website at:{' '}
                <a href="https://www.ftc.gov/enforcement/rules/rulemaking-regulatory-reform-proceedings/childrens-online-privacy-protection-rule">
                  https://www.ftc.gov/enforcement/rules/rulemaking-regulatory-reform-proceedings/childrens-online-privacy-protection-rule
                </a>
                .
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Updates and changes to this policy</h2>
              <p>
                We may add to, change, update, or modify this Policy to reflect any changes to how we treat your
                information or in response to changes in law. Should this Policy change, we will post all changes to
                this Policy on this page. Any such changes, updates, or modifications will be effective immediately upon
                posting. The date on which this Policy was last modified is identified at the beginning of this Policy.
              </p>
              <p>
                You are expected to, and you acknowledge and agree that it is your responsibility to, carefully review
                this Policy prior to using the Site, and from time to time, so that you are aware of any changes. Your
                continued use of the Site after the &quot;Last Updated&quot; date will constitute your acceptance of and
                agreement to such changes and to our collection and sharing of your information according to the terms
                of the then-current Policy. If you do not agree with this Policy and our practices, you should not use
                the Site.
              </p>
            </div>

            <div className="legal-page__section">
              <h2>Contact us</h2>
              <p>
                For more information, or if you have any questions or concerns regarding this Policy, you may contact
                us using the information below, and we will do our best to assist you. Please note, if your communication
                is sensitive, you may wish to contact us by postal mail or telephone.
              </p>
              <ul className="legal-page__list">
                <li>
                  <strong>In Writing:</strong> Attn: Chief Operating Officer, 8027 Cooper Creek Blvd, Suite 110, University Park,
                  Florida 34201
                </li>
                <li>
                  <strong>By Email:</strong> info@phennx.io
                </li>
              </ul>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
